/** XeAgents.ai**/
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "wouter";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { AuthStorage } from "@/lib/auth-storage";
import { formatDate, formatRelativeTime } from "@/lib/date-format";
import {
  ArrowLeft,
  Clock,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Send,
  XCircle,
  User,
  Calendar,
  Tag,
  Loader2,
  RefreshCw,
  RotateCcw,
} from "lucide-react";

interface TicketReply {
  id: string;
  ticketId: string;
  userId: string;
  message: string;
  isStaffReply: boolean;
  isInternal: boolean;
  attachments: string[];
  createdAt: string;
  userName?: string;
  userEmail?: string;
}

interface TicketUser {
  name: string;
  email: string;
}

interface TicketDetail {
  id: string;
  ticketNumber: number;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  resolution: string | null;
  attachments: string[];
  assignedTo: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  user: TicketUser;
  replies: TicketReply[];
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<any> }> = {
  open: { label: "Open", color: "bg-blue-500", icon: AlertCircle },
  in_progress: { label: "In Progress", color: "bg-yellow-500", icon: Clock },
  waiting_on_customer: { label: "Waiting on Customer", color: "bg-purple-500", icon: MessageSquare },
  resolved: { label: "Resolved", color: "bg-green-500", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-gray-500", icon: XCircle },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-gray-400" },
  medium: { label: "Medium", color: "bg-blue-400" },
  high: { label: "High", color: "bg-orange-500" },
  urgent: { label: "Urgent", color: "bg-red-500" },
};

const categoryConfig: Record<string, { label: string }> = {
  general: { label: "General" },
  billing: { label: "Billing" },
  technical: { label: "Technical" },
  feature_request: { label: "Feature Request" },
  bug_report: { label: "Bug Report" },
  account: { label: "Account" },
};

export default function TicketDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const currentUser = AuthStorage.getUser();
  const isSuperAdmin = AuthStorage.isSuperAdmin();

  const [replyMessage, setReplyMessage] = useState("");

  // Fetch ticket details
  const { data: ticket, isLoading, refetch } = useQuery<TicketDetail>({
    queryKey: ["helpdesk-ticket", id],
    queryFn: async () => {
      const res = await fetch(`/api/helpdesk/tickets/${id}`, {
        headers: {
          Authorization: AuthStorage.getAuthHeader() || "",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch ticket");
      return res.json();
    },
    enabled: !!id,
  });

  // Add reply mutation
  const replyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/helpdesk/tickets/${id}/replies`, {
        message: replyMessage,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: t("helpdesk.replySent"),
        description: t("helpdesk.replySentDesc"),
      });
      setReplyMessage("");
      queryClient.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
    },
    onError: (error: any) => {
      toast({
        title: t("helpdesk.error"),
        description: error.message || t("helpdesk.replyFailed"),
        variant: "destructive",
      });
    },
  });

  // Close ticket mutation
  const closeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/helpdesk/tickets/${id}/close`, {});
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: t("helpdesk.ticketClosed"),
        description: t("helpdesk.ticketClosedDesc"),
      });
      queryClient.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
    },
    onError: (error: any) => {
      toast({
        title: t("helpdesk.error"),
        description: error.message || t("helpdesk.closeFailed"),
        variant: "destructive",
      });
    },
  });

  // Reopen ticket mutation
  const reopenMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/helpdesk/tickets/${id}/reopen`, {});
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: t("helpdesk.ticketReopened"),
        description: t("helpdesk.ticketReopenedDesc"),
      });
      queryClient.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
    },
    onError: (error: any) => {
      toast({
        title: t("helpdesk.error"),
        description: error.message || t("helpdesk.reopenFailed"),
        variant: "destructive",
      });
    },
  });

  const handleSendReply = () => {
    if (!replyMessage.trim()) {
      toast({
        title: t("helpdesk.error"),
        description: t("helpdesk.messageRequired"),
        variant: "destructive",
      });
      return;
    }
    replyMutation.mutate();
  };

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.open;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const config = priorityConfig[priority] || priorityConfig.medium;
    return (
      <Badge variant="outline" className={`border-0 ${config.color} text-white`}>
        {config.label}
      </Badge>
    );
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">{t("helpdesk.ticketNotFound")}</h3>
            <Button className="mt-4" onClick={() => setLocation("/app/helpdesk")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("helpdesk.backToTickets")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canReply = ticket.status !== "closed";
  const canClose = ticket.status !== "closed" && ticket.status !== "resolved";
  const canReopen = ticket.status === "closed" || ticket.status === "resolved";

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => setLocation("/app/helpdesk")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground font-mono">#{ticket.ticketNumber}</span>
            {getStatusBadge(ticket.status)}
            {getPriorityBadge(ticket.priority)}
          </div>
          <h1 className="text-xl font-bold mt-1">{ticket.subject}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canReopen && (
            <Button variant="outline" onClick={() => reopenMutation.mutate()}>
              <RotateCcw className="h-4 w-4 mr-2" />
              {t("helpdesk.reopen")}
            </Button>
          )}
          {canClose && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline">
                  <XCircle className="h-4 w-4 mr-2" />
                  {t("helpdesk.closeTicket")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("helpdesk.closeTicketConfirm")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("helpdesk.closeTicketConfirmDesc")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => closeMutation.mutate()}>
                    {t("helpdesk.closeTicket")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Original Ticket */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>{getInitials(ticket.user.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium">{ticket.user.name}</div>
                    <div className="text-sm text-muted-foreground">{ticket.user.email}</div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatDate(ticket.createdAt, "PPp")}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap">
                {ticket.description}
              </div>
            </CardContent>
          </Card>

          {/* Resolution (if resolved) */}
          {ticket.resolution && (
            <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
              <CardHeader>
                <CardTitle className="text-green-700 dark:text-green-400 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  {t("helpdesk.resolution")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap">
                  {ticket.resolution}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Replies */}
          {ticket.replies.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  {t("helpdesk.conversation")} ({ticket.replies.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-4">
                    {ticket.replies.map((reply, index) => (
                      <div key={reply.id}>
                        {index > 0 && <Separator className="my-4" />}
                        <div className="flex gap-3">
                          <Avatar className={reply.isStaffReply ? "ring-2 ring-primary" : ""}>
                            <AvatarFallback>
                              {getInitials(reply.userName || "User")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{reply.userName || "User"}</span>
                                {reply.isStaffReply && (
                                  <Badge variant="secondary" className="text-xs">
                                    {t("helpdesk.staff")}
                                  </Badge>
                                )}
                              </div>
                              <span className="text-sm text-muted-foreground">
                                {formatRelativeTime(reply.createdAt)}
                              </span>
                            </div>
                            <div className="mt-2 prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap">
                              {reply.message}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Reply Form */}
          {canReply && (
            <Card>
              <CardHeader>
                <CardTitle>{t("helpdesk.addReply")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Textarea
                    placeholder={t("helpdesk.replyPlaceholder")}
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={4}
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleSendReply} disabled={replyMutation.isPending}>
                      {replyMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {t("helpdesk.sendReply")}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("helpdesk.ticketDetails")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("helpdesk.ticketId")}</span>
                <span className="font-mono">#{ticket.ticketNumber}</span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("helpdesk.status")}</span>
                {getStatusBadge(ticket.status)}
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("helpdesk.priority")}</span>
                {getPriorityBadge(ticket.priority)}
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("helpdesk.category")}</span>
                <Badge variant="outline">
                  {categoryConfig[ticket.category]?.label || ticket.category}
                </Badge>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("helpdesk.created")}</span>
                <span className="text-sm">
                  {formatDate(ticket.createdAt, "PP")}
                </span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("helpdesk.lastActivity")}</span>
                <span className="text-sm">
                  {formatRelativeTime(ticket.lastActivityAt)}
                </span>
              </div>
              {ticket.resolvedAt && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t("helpdesk.resolved")}</span>
                    <span className="text-sm">
                      {formatDate(ticket.resolvedAt, "PP")}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
